/**
 * Pure reducer for PromptStudioDialog (lot B step 2).
 * No React, no API calls.
 *
 * @see docs/plans/prompt-studio-build/B-popup-frontend.md §Files and props studioReducer.ts
 */

import type {
  BrandQuery,
  BrandQueryAxis,
  AllowanceFigures,
  FirstRunNotice,
  SaveWarnings,
  BulkItem,
} from '@traaker/shared';
import { mergeFirstRun } from './promptStudioModel';
import type { FilterState } from './filterModel';
import { EMPTY_FILTER } from './filterModel';

// ---------------------------------------------------------------------------
// Tab type
// ---------------------------------------------------------------------------

export type StudioTab = 'conversations' | 'prompts' | 'coverage';
export type StudioBody = 'list' | 'composer' | 'review';

// ---------------------------------------------------------------------------
// StudioCall — the ONE next API call the dialog will make
// ---------------------------------------------------------------------------

export type StudioCall =
  | { op: 'bulk'; dryRun: boolean; items: BulkItem[]; seq?: number }
  | { op: 'classify'; queryId: string; body: object }
  | { op: 'patch';   queryId: string; body: object }
  | { op: 'delete';  axis: BrandQueryAxis | 'pending'; queryId: string };

// ---------------------------------------------------------------------------
// ReviewPreview
// ---------------------------------------------------------------------------

/**
 * The ONE preview of the current review batch (step 7).
 * Opening the review and every reviewChanged bump `seq` and queue a new
 * `bulk` items dryRun with that seq.  A preview result whose seq is not
 * the current one is dropped.
 */
export interface ReviewPreview {
  seq: number;
  status: 'pending' | 'done' | 'failed';
  figuresAfter?: AllowanceFigures;
  refused?:      boolean;
  warnings?:     SaveWarnings[];
  firstRun?:     FirstRunNotice[];
}

// ---------------------------------------------------------------------------
// ReviewRow
// ---------------------------------------------------------------------------

export interface ReviewRow {
  text:       string;
  axis?:      BrandQueryAxis;
  intent?:    string;
  language?:  string;
  complements?: string[];
  topic?:     string;
  criteria?:  string[];
  targetId?:  string;
  targetCategory?: string;
  /** The Cibles search failed at classification (lot F): « recherche en échec, réessayer ». */
  targetLookup?: 'failed';
  zoneMeta?:  unknown;
  error?:     string;
}

// ---------------------------------------------------------------------------
// LimitState
// ---------------------------------------------------------------------------

export interface LimitState {
  where: 'review' | 'edit' | string;   // 'review' | 'edit' | <root queryId>
  after: AllowanceFigures;
  current: AllowanceFigures | null;
}

// ---------------------------------------------------------------------------
// StudioState
// ---------------------------------------------------------------------------

export interface StudioState {
  tab:    StudioTab;
  body:   StudioBody;
  openId: string | null;         // expanded family root queryId
  q:      string;                // free-text search
  filter: FilterState;

  review:   ReviewRow[];         // current review rows
  preview:  ReviewPreview | null;

  quotaDialog:     SaveWarnings[] | null;  // agency only
  limit:           LimitState | null;      // self-serve refusal state
  previewFirstRun: FirstRunNotice[] | null; // current preview's firstRun
  firstRunNotice:  FirstRunNotice[] | null; // merged save firstRun for banner

  next:  StudioCall | null;      // the one in-flight call
  queue: StudioCall[];           // pending calls
  rowErrors: Record<string, string>;  // queryId → error
  lineError: string | null;          // amber line error above foot
}

// ---------------------------------------------------------------------------
// Initial state
// ---------------------------------------------------------------------------

export function initialStudioState(tab: StudioTab): StudioState {
  return {
    tab,
    body:   'list',
    openId: null,
    q:      '',
    filter: EMPTY_FILTER,
    review: [],
    preview: null,
    quotaDialog: null,
    limit: null,
    previewFirstRun: null,
    firstRunNotice: null,
    next: null,
    queue: [],
    rowErrors: {},
    lineError: null,
  };
}

// ---------------------------------------------------------------------------
// StudioAction
// ---------------------------------------------------------------------------

export type StudioAction =
  | { type: 'setTab'; tab: StudioTab }
  | { type: 'setBody'; body: StudioBody }
  | { type: 'setOpenId'; id: string | null }
  | { type: 'setQ'; q: string }
  | { type: 'setFilter'; filter: FilterState }
  | { type: 'openReview'; rows: ReviewRow[] }
  | { type: 'reviewChanged'; rows: ReviewRow[] }
  | { type: 'submitBatch' }
  | { type: 'submitEdit'; queryId: string; body: object }
  | { type: 'saveZone'; queryId: string; axis: BrandQueryAxis | 'pending'; zoneMeta: unknown }
  | { type: 'disable'; queryId: string; axis: BrandQueryAxis | 'pending' }
  | { type: 'reactivate'; queryId: string; axis: BrandQueryAxis | 'pending' }
  | { type: 'confirmDelete'; queryId: string; axis: BrandQueryAxis | 'pending' }
  | { type: 'callOk'; result: unknown }
  | { type: 'callFailed'; err: unknown }
  | { type: 'confirm' }
  | { type: 'cancel' }
  | { type: 'firstRunSaved'; firstRun?: FirstRunNotice[] }
  | { type: 'dismissFirstRun' };

// ---------------------------------------------------------------------------
// studioReducer
// ---------------------------------------------------------------------------

let _previewSeq = 0;

/**
 * Pure reducer for PromptStudioDialog.
 *
 * Every click sequence (save, delete, Désactiver, Réactiver, the quota dialog)
 * is a state transition here; the component only runs the ONE call `s.next`
 * names and dispatches its result.
 */
export function studioReducer(s: StudioState, a: StudioAction): StudioState {
  switch (a.type) {
    case 'setTab':
      return {
        ...s,
        tab: a.tab,
        firstRunNotice: null,   // cleared on tab change
      };

    case 'setBody':
      return { ...s, body: a.body };

    case 'setOpenId':
      return { ...s, openId: a.id };

    case 'setQ':
      return { ...s, q: a.q };

    case 'setFilter':
      return { ...s, filter: a.filter };

    case 'openReview': {
      const seq = ++_previewSeq;
      return {
        ...s,
        body: 'review',
        review: a.rows,
        preview: { seq, status: 'pending' },
        limit: null,
        firstRunNotice: null,  // cleared on new review
        // Queue the dryRun preview
        next: s.next ?? { op: 'bulk', dryRun: true, items: rowsToBulkItems(a.rows), seq },
      };
    }

    case 'reviewChanged': {
      const seq = ++_previewSeq;
      return {
        ...s,
        review: a.rows,
        preview: { seq, status: 'pending' },
        limit: null,
        // Queue a new preview
        next: s.next ?? { op: 'bulk', dryRun: true, items: rowsToBulkItems(a.rows), seq },
        queue: s.next
          ? [...s.queue, { op: 'bulk', dryRun: true, items: rowsToBulkItems(a.rows), seq }]
          : [],
      };
    }

    case 'submitBatch': {
      // No-op while a saving chunk is in flight or preview not done
      if (s.next) return s;
      if (!s.preview || s.preview.status !== 'done') return s;
      if (s.limit) return s;

      // Agency: open QuotaWarningDialog if preview has warnings
      if (s.preview.warnings?.length) {
        return { ...s, quotaDialog: s.preview.warnings };
      }

      const items = rowsToBulkItems(s.review);
      return {
        ...s,
        firstRunNotice: null,  // cleared before save
        next: { op: 'bulk', dryRun: false, items },
      };
    }

    case 'submitEdit': {
      return {
        ...s,
        firstRunNotice: null,
        next: s.next ?? { op: 'classify', queryId: a.queryId, body: a.body },
      };
    }

    case 'saveZone': {
      // saveZone with placeKey(pick) === placeKey(root) → queues nothing (R3)
      // Caller is responsible for checking R3 before dispatching
      return {
        ...s,
        firstRunNotice: null,
        next: s.next ?? {
          op: 'patch',
          queryId: a.queryId,
          body: { axis: a.axis, zoneMeta: a.zoneMeta },
        },
      };
    }

    case 'disable': {
      return {
        ...s,
        next: s.next ?? {
          op: 'patch',
          queryId: a.queryId,
          body: { axis: a.axis, status: 'archived' },
        },
      };
    }

    case 'reactivate': {
      // EVERY reactivate previews first (C11 status preview: true)
      return {
        ...s,
        next: s.next ?? {
          op: 'patch',
          queryId: a.queryId,
          body: { axis: a.axis, status: 'active', preview: true },
        },
      };
    }

    case 'confirmDelete': {
      return {
        ...s,
        next: s.next ?? {
          op: 'delete',
          axis: a.axis,
          queryId: a.queryId,
        },
      };
    }

    case 'callOk': {
      const result = a.result as any;
      // Merge firstRun if the response carried it
      const newFirstRun = mergeFirstRun(s.firstRunNotice, result?.firstRun);
      return {
        ...s,
        next: s.queue[0] ?? null,
        queue: s.queue.slice(1),
        rowErrors: {},
        lineError: null,
        firstRunNotice: newFirstRun,
        // Update preview status if this was a preview call
        preview: s.preview?.status === 'pending' ? { ...s.preview, status: 'done', ...result } : s.preview,
      };
    }

    case 'callFailed': {
      return {
        ...s,
        next: null,
        queue: [],
        lineError: 'promptStudio.common.saveFailed',
      };
    }

    case 'confirm': {
      // Close QuotaWarningDialog and proceed with the save
      if (!s.quotaDialog) return s;
      const items = rowsToBulkItems(s.review);
      return {
        ...s,
        quotaDialog: null,
        firstRunNotice: null,
        next: { op: 'bulk', dryRun: false, items },
      };
    }

    case 'cancel': {
      return {
        ...s,
        quotaDialog: null,
        body: s.body === 'review' ? 'composer' : s.body,
      };
    }

    case 'firstRunSaved': {
      return {
        ...s,
        firstRunNotice: a.firstRun ?? null,
      };
    }

    case 'dismissFirstRun': {
      return { ...s, firstRunNotice: null };
    }

    default:
      return s;
  }
}

// ---------------------------------------------------------------------------
// deleteOrder
// ---------------------------------------------------------------------------

/**
 * Order for deleting a family: variants first, root last (C11).
 * Variants are rows whose queryId !== the family root's queryId.
 */
export function deleteOrder(f: { root: BrandQuery; rows: BrandQuery[] }): BrandQuery[] {
  const variants = f.rows.filter((q) => q.queryId !== f.root.queryId);
  return [...variants, f.root];
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function rowsToBulkItems(rows: ReviewRow[]): BulkItem[] {
  return rows.map((r) => ({
    text:       r.text,
    intent:     r.intent as any,
    language:   r.language,
    complements: r.complements as any,
    criteria:   r.criteria,
    targetId:   r.targetId,
    zoneMeta:   r.zoneMeta as any,
  }));
}
